import { Flex, Box, Text, Button, Grid, Spinner, Icon, Tooltip } from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import { IconInfo } from 'assets/icons';
import { IconInfo } from 'assets/icons';
import { FormikCheckbox } from 'components/Formik';
import Loader from 'components/Loader';
import { routePaths } from 'global/routePaths';
import { useDisconnectRUNByADP } from 'hooks/integrations/RUNByADP/useDisconnectRUNByADP';
import { useEnableDisableDepartments } from 'hooks/integrations/RUNByADP/useEnableDisableDepartments';
import { useFetchRUNByADPSettings } from 'hooks/integrations/RUNByADP/useFetchRUNByADPSetting';
import { useCompanyInfo } from 'store/useUserStore';

import { initialRUNByADPSettingsData } from './schema';
import { initialRUNByADPData } from '../MappingTabs/Earnings/schema';

const RunByADPSettings: React.FC<unknown> = () => {
  const [formData, setFormData] = useState<any>(initialRUNByADPData);

  const { t } = useTranslation(['common', 'integrations']);

  const companyInfo = useCompanyInfo();
  const navigate = useNavigate();

  const { status: enableDisableDepartmentsStatus, mutateAsync: mutateAsyncEnableDisableDepartments } =
    useEnableDisableDepartments();
  const { mutate: disconnectRUNByADP, status: disconnectRUNByADPStatus } = useDisconnectRUNByADP();
  const { isFetching: isFetchingSettings, refetch } = useFetchRUNByADPSettings({
    onSettled: (data: any) => {
      if (data) {
        const settingData = data?.payload;

        const pFormData = {
          export_mileage_only: settingData?.export_mileage_only ? ['1'] : [],
          departments_enabled: settingData?.departments_enabled ? ['1'] : [],
        };

        setFormData(pFormData);
      }
    },
  });

  const handleFormSubmit = async (values: typeof initialRUNByADPSettingsData, { resetForm }: any) => {
    const postData = {
      export_mileage_only: values.export_mileage_only.length > 0 ? true : false,
      departments_enabled: values.departments_enabled.length > 0 ? true : false,
    };

    mutateAsyncEnableDisableDepartments(postData).then(() => {
      resetForm();
      refetch();
    });
  };

  const onConfigureMapping = () => {
    navigate(routePaths.integrations.RUNByADP.mapping);
  };

  const onDisable = () => {
    disconnectRUNByADP();
  };

  return (
    <Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Flex flexDir="column" w="full">
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px" mb={6} w="full">
            <Text fontSize="sm" color="gray.500" mb={4}>
              {companyInfo?.name}
            </Text>
            <Text mb={4} color="primary.300" cursor="pointer" onClick={onConfigureMapping}>
              {t('integrations:configureMappings')}
            </Text>
            <Text color="primary.300" onClick={onDisable} cursor="pointer">
              {t('integrations:RUNByADP.disconnectFromRUNPoweredByADP')}
              {disconnectRUNByADPStatus === 'loading' && <Spinner size="xs" />}
            </Text>
          </Box>
        </Flex>
        <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
          <Text fontSize="md" mb={4}>
            {t('common:settings')}
          </Text>
          {isFetchingSettings ? (
            <Loader height="100px" />
          ) : (
            <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
              {() => (
                <Form noValidate>
                  <FormikCheckbox
                    options={[
                      {
                        label: (
                          <Flex alignItems="center">
                            Export Mileage Only
                            <Tooltip
                              hasArrow
                              bg="black"
                              label="If 'Export Mileage Only' is selected, other settings won't apply, even if enabled."
                            >
                              <Box as="span" ml="1">
                                <Icon as={IconInfo} />
                              </Box>
                            </Tooltip>
                          </Flex>
                        ),
                        value: '1',
                      },
                    ]}
                    name="export_mileage_only"
                  />
                  <FormikCheckbox
                    options={[{ label: t('integrations:RUNByADP.enablePayRates'), value: '1' }]}
                    name="departments_enabled"
                  />
                  <Button
                    type="submit"
                    colorScheme="secondary"
                    isLoading={enableDisableDepartmentsStatus === 'loading'}
                    minWidth="100px"
                  >
                    {t('common:save')}
                  </Button>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default RunByADPSettings;
